import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Phone, Menu, X, ChevronDown } from "lucide-react";
import { assets, business } from "../../data/site";
import { useOrder } from "../OrderProvider";
import BrandLogo from "../BrandLogo";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "Our Story" },
  {
    label: "Menu",
    match: "/menu",
    children: [
      { to: "/menu/combos", label: "Combo Meals" },
      { to: "/menu/a-la-carte", label: "À La Carte" },
    ],
  },
  { to: "/catering", label: "Catering" },
  { to: "/featured", label: "Food Bloggers" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const openOrder = useOrder();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 bg-[#0a0a0a]/95 backdrop-blur-xl border-b border-white/10"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="flex items-center justify-between h-20 md:h-24 gap-4">
          {/* Logo */}
          <Link to="/" data-testid="logo-link" className="flex items-center shrink-0">
            <BrandLogo src={assets.logo} size="md" />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex flex-1 items-center justify-center gap-7 xl:gap-9">
            {links.map((l) =>
              l.children ? (
                <div key={l.label} className="relative group">
                  <button
                    data-testid={`nav-${l.label.toLowerCase()}`}
                    className={`flex items-center gap-1 text-base xl:text-lg font-medium whitespace-nowrap transition-colors ${
                      location.pathname.startsWith(l.match) ? "text-[#ffd100]" : "text-white group-hover:text-[#ffd100]"
                    }`}
                  >
                    {l.label} <ChevronDown size={16} className="transition-transform group-hover:rotate-180" />
                  </button>
                  {/* dropdown */}
                  <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                    <div className="bg-[#141414] border border-white/10 rounded-xl shadow-2xl overflow-hidden min-w-[190px]">
                      {l.children.map((c) => (
                        <NavLink
                          key={c.to}
                          to={c.to}
                          data-testid={`nav-${c.to.split("/").pop()}`}
                          className={({ isActive }) =>
                            `block px-5 py-3.5 text-base font-medium transition-colors ${
                              isActive ? "bg-[#ffd100] text-[#0a0a0a]" : "text-white hover:bg-white/5 hover:text-[#ffd100]"
                            }`
                          }
                        >
                          {c.label}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `text-base xl:text-lg font-medium whitespace-nowrap transition-colors ${
                      isActive ? "text-[#ffd100]" : "text-white hover:text-[#ffd100]"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              )
            )}
          </nav>

          {/* Actions + mobile toggle */}
          <div className="flex items-center gap-2 md:gap-3">
            <a
              href={`tel:${business.phoneRaw}`}
              data-testid="header-call-btn"
              className="hidden md:flex items-center gap-2 border border-[#ffd100]/60 text-[#ffd100] font-semibold px-4 py-2.5 rounded-full hover:bg-[#ffd100] hover:text-[#0a0a0a] transition-colors whitespace-nowrap"
            >
              <Phone size={18} strokeWidth={2.6} />
              <span className="hidden xl:inline">{business.phone}</span>
              <span className="xl:hidden">Call</span>
            </a>
            <button
              type="button"
              onClick={openOrder}
              data-testid="header-order-online-btn"
              className="hidden sm:flex items-center gap-2 bg-[#ffd100] text-[#0a0a0a] font-bold px-5 py-2.5 rounded-full hover:bg-[#e5bc00] transition-colors whitespace-nowrap"
            >
              Order Online
            </button>
            <button
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden text-white p-2"
            >
              {open ? <X size={30} /> : <Menu size={30} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile nav */}
      {open && (
        <nav data-testid="mobile-nav" className="lg:hidden bg-[#0a0a0a] border-t border-white/10 px-5 py-4">
          {links.map((l) =>
            l.children ? (
              <div key={l.label} className="border-b border-white/5 py-3">
                <p className="text-neutral-400 text-sm font-semibold uppercase tracking-wider mb-2">{l.label}</p>
                {l.children.map((c) => (
                  <NavLink
                    key={c.to}
                    to={c.to}
                    data-testid={`mobile-nav-${c.to.split("/").pop()}`}
                    className={({ isActive }) =>
                      `block py-3 pl-3 text-lg font-medium ${isActive ? "text-[#ffd100]" : "text-white"}`
                    }
                  >
                    {c.label}
                  </NavLink>
                ))}
              </div>
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `block py-4 text-xl font-medium border-b border-white/5 ${isActive ? "text-[#ffd100]" : "text-white"}`
                }
              >
                {l.label}
              </NavLink>
            )
          )}
          <button
            type="button"
            onClick={() => { setOpen(false); openOrder(); }}
            className="mt-4 w-full flex items-center justify-center gap-2 bg-[#ffd100] text-[#0a0a0a] font-bold px-5 py-4 rounded-full text-lg"
          >
            Order Online
          </button>
          <a
            href={`tel:${business.phoneRaw}`}
            className="mt-3 flex items-center justify-center gap-2 border border-[#ffd100]/60 text-[#ffd100] font-bold px-5 py-4 rounded-full text-lg"
          >
            <Phone size={20} /> {business.phone}
          </a>
        </nav>
      )}
    </header>
  );
}
